import { parseBlob } from 'music-metadata-browser';

export interface Mp3Metadata {
  title: string;
  artist: string;
  album: string;
  pictureUrl: string | null;
  pictureBlob: Blob | null;
  duration: number;
  bitrate?: number;
  sampleRate?: number;
  numberOfChannels?: number;
  codec?: string;
  year?: number;
  genre?: string;
  comment?: string;
  bpm?: number;
  trackNo?: number;
  diskNo?: number;
  albumArtist?: string;
  composer?: string;
  lyricist?: string;
  isrc?: string;
}

function firstString(v: unknown): string | undefined {
  if (typeof v === 'string') return v;
  if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'string') return v[0];
  return undefined;
}

function firstNumber(v: unknown): number | undefined {
  if (typeof v === 'number' && !isNaN(v)) return v;
  if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'number') return v[0];
  return undefined;
}

export async function readMp3Metadata(file: File): Promise<Mp3Metadata> {
  const metadata = await parseBlob(file);
  const format = metadata.format;
  const common = metadata.common;

  let pictureUrl: string | null = null;
  let pictureBlob: Blob | null = null;
  const picture = common.picture?.[0];
  if (picture?.data) {
    const fmt = picture.format || 'image/jpeg';
    const blob = new Blob([picture.data], { type: fmt });
    pictureBlob = blob;
    pictureUrl = URL.createObjectURL(blob);
  }

  const genreArr = common.genre;
  const genre = Array.isArray(genreArr) && genreArr.length > 0
    ? (typeof genreArr[0] === 'string' ? genreArr[0] : String(genreArr[0]))
    : undefined;

  const commentVal = common.comment;
  const comment = Array.isArray(commentVal) && commentVal.length > 0
    ? (typeof commentVal[0] === 'string' ? commentVal[0] : String(commentVal[0]))
    : firstString(commentVal);

  const isrcVal = common.isrc;
  const isrc = Array.isArray(isrcVal) && isrcVal.length > 0
    ? (typeof isrcVal[0] === 'string' ? isrcVal[0] : undefined)
    : undefined;

  return {
    title:
      common.title ||
      file.name.replace(/\.mp3$/i, '').replace(/[-_]/g, ' '),
    artist: common.artist || '',
    album: common.album || '',
    pictureUrl,
    pictureBlob,
    duration: Math.floor(format.duration ?? 0),
    bitrate: format.bitrate,
    sampleRate: format.sampleRate,
    numberOfChannels: format.numberOfChannels,
    codec: format.codec,
    year: common.year,
    genre,
    comment,
    bpm: firstNumber(common.bpm) ?? common.bpm,
    trackNo: common.track?.no ?? undefined,
    diskNo: common.disk?.no ?? undefined,
    albumArtist: firstString(common.albumartist),
    composer: firstString(common.composer),
    lyricist: firstString(common.lyricist),
    isrc,
  };
}
